import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { GET_BUDGET_ITEMS } from '../../../../gql/queries/budget/budgetItems';
import { GET_BUDGET_CATEGORIES } from '../../../../gql/queries/budget/budgetCategories';

// Utils
import { AuthContextType } from '../../../../hooks/useAuth';

// Constants
import { formBudgetItemManagementNaming } from '../../../../components/forms/AddBudgetItemForm/constants';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';

const loadTableData = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const { data } = await client.query({
        query: GET_BUDGET_ITEMS,
        fetchPolicy: 'network-only',
        variables: {
          filters: null,
          pagination: { page: 1, limit: 10000 }, // Límite explícito para evitar límite por defecto de 50
        },
      });

      // Verificar que tenemos items antes de procesar
      if (!data?.budgetItems?.items) {
        return [];
      }

      // Ordenar por código
      const sortedItems = [...data.budgetItems.items].sort((a, b) =>
        a.code.localeCompare(b.code)
      );

      const processedItems = sortedItems.map((item, index) => {
        const processed = {
          id: item.id,
          code: item.code,
          description: item.description,
          unit: item.unit,
          unitPrice: item.unitPrice,
          isActive: item.isActive,
          budgetCategory: item.budgetCategory,
          budgetCategoryId: item.budgetCategoryId,
          actionBudgetItemsCount: item.actionBudgetItemsCount || 0,
          usageCount: item.usageCount || 0,
          state: item.isActive ? 'active' : 'inactive',
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        };

        return processed;
      });

      return processedItems;
    }

    return [];
  } catch (err) {
    Sentry.captureException(err);
    return [];
  }
};

const loadCategories = async () => {
  try {
    const {
      data: { budgetCategories },
    } = await client.query({
      query: GET_BUDGET_CATEGORIES,
    });

    return budgetCategories;
  } catch (err) {
    Sentry.captureException(err);
    return [];
  }
};

export const BudgetItemsManagementLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  try {
    const [data, categories] = await Promise.all([
      loadTableData(userData),
      loadCategories(),
    ]);

    const activeItems = data.filter(
      (item: { isActive: boolean }) => item.isActive
    );
    const inactiveItems = data.filter(
      (item: { isActive: boolean }) => !item.isActive
    );

    let cacheData =
      sessionStorage.getItem(
        formBudgetItemManagementNaming.NEW_BUDGET_ITEM_MANAGEMENT_FORM
      ) || {};

    if (Object.keys(cacheData).length > 0) {
      cacheData = JSON.parse(decompressSessionStorage(cacheData as string));
    }

    const finalResult = {
      activeItems,
      inactiveItems,
      categories,
      cacheData: {
        ...cacheData,
      },
    };

    return finalResult;
  } catch (error) {
    Sentry.captureException(error);

    // Return empty data in case of error
    return {
        activeItems: [],
        inactiveItems: [],
        categories: [],
        cacheData: {},
      };
  }
};

  return { data: load() };
};
